/**
 * Namespaced browser storage for host applications.
 *
 * Apps keep their own state next to the SDK's — a remembered tab, a notification cooldown, a cached
 * account list. Under plain keys that state is shared by every account on the origin and survives
 * logout, which shows up as one account's snackbar cooldown suppressing another's, and a signed-out
 * account's data being inherited by the next one in the tab.
 *
 * Going through here puts it under the same `bayun:v1:<appId>:s:<sessionId>:` prefix as the SDK's
 * own state, so it is separated per account and per app and the logout prefix scan removes it.
 *
 * NOT for secrets — values are stored verbatim. Key material belongs in saveInStorage, which
 * encrypts against the session's storage key.
 */

import {
  clearFlowScope,
  readFlowValue,
  readSessionValue,
  removeSessionValue,
  writeFlowValue,
  writeSessionValue,
} from "./SessionStorage";
import { getSessionMode } from "./SessionRegistry";

/**
 * The cached list of member accounts linked to a session, shown in the account switcher. Both the
 * SDK and its host apps write it, so the key lives here rather than being spelled out on each side.
 */
export const LINKED_MEMBERS_KEY = "linkedMembers";

/** Belongs to one account and goes when it logs out. The right scope for almost everything. */
export const getSessionItem = (sessionId, identifier) =>
  readSessionValue(sessionId, identifier, getSessionMode(sessionId));

export const setSessionItem = (sessionId, identifier, value) => {
  writeSessionValue(sessionId, identifier, value, getSessionMode(sessionId));
};

export const removeSessionItem = (sessionId, identifier) => {
  removeSessionValue(sessionId, identifier, getSessionMode(sessionId));
};

/**
 * For activity that happens before there is a session to attach it to — signup, today. Flow values
 * live in sessionStorage, so two tabs can run two flows without seeing each other and closing the
 * tab discards it.
 *
 * The flow id can be a constant: per-tab isolation already comes from sessionStorage.
 */
export const getFlowItem = (flowId, identifier) => readFlowValue(flowId, identifier);

export const setFlowItem = (flowId, identifier, value) => {
  writeFlowValue(flowId, identifier, value);
};

export const clearFlow = (flowId) => clearFlowScope(flowId);
